import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { fontMerriweather, fontRoboto, fontRobotoCondensed } from "@product/scmp-sdk";

import { BaseImage } from "~/components/common/base-image";
import { Drawer } from "~/components/common/drawer";
import { DrawerCard } from "~/components/common/drawer/styles";
import { SponsorTag } from "~/components/sponsor-tag";
import {
  alignmentCssVariableValues,
  fullViewportWidthWithMaxPageWidthPaddingInline,
  setFullViewportWidthWithMaxPageWidthStyles,
} from "~/lib/styles";

import { TopicHeaderRelatedLinks } from "./topic-header-related-links";

// Content
export const Label = styled.p`
  padding-block: 16px;

  color: #001246;
  font-weight: 700;
  font-size: 20px;
  font-family: ${fontRobotoCondensed};
  line-height: 24px;
  text-transform: uppercase;

  border-block-end: 4px solid #001246;

  ${props => props.theme.breakpoints.up("tablet")} {
    padding-block: 0;

    font-size: 18px;

    border-block-end: none;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    font-size: 20px;
  }
`;

export const TitleContainer = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  align-items: flex-start;
`;

export const Title = styled.div`
  position: relative;

  font-size: 26px;
  font-family: ${fontRoboto};
  line-height: 30px;

  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 800px;

    font-size: 28px;
    font-family: ${fontRoboto};
    line-height: 35px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    max-inline-size: 800px;

    font-size: 48px;
    font-family: ${fontRoboto};
    line-height: 57px;
  }
`;

export const ImageIcon = styled(BaseImage)`
  inline-size: 60px;
  block-size: 60px;

  border-radius: 50%;

  object-fit: cover;
  object-position: 50%;

  ${props => props.theme.breakpoints.up("tablet")} {
    display: none;
  }
`;

export const ImageIconContainer = styled.div`
  margin-inline-start: 20px;
`;

export const Info = styled.span`
  position: absolute;

  display: inline-block;

  inline-size: 16px;
  block-size: 16px;
  margin-block-start: 6px;
  margin-inline-start: 6px;

  color: #4585ff;
  font-size: 12px;
  font-family: ${fontMerriweather};
  font-style: italic;
  line-height: 16px;
  text-align: center;
  vertical-align: middle;

  border: 1px solid #4585ff;
  border-radius: 50%;

  cursor: pointer;

  ${props => props.theme.breakpoints.up("tablet")} {
    display: none;
  }
`;

export const Description = styled.div`
  font-size: 14px;
  font-family: ${fontRoboto};
  line-height: 18px;
`;

export const StyledSponsorTag = styled(SponsorTag)``;

type ContentProps = {
  $hasBackgroundImage?: boolean;
};

export const Content = styled.div<ContentProps>`
  > * + :not(:empty) {
    margin-block-start: 16px;
  }

  ${props => css`
    color: ${props.$hasBackgroundImage ? "#fff" : "#000"};
  `}

  ${Label} {
    color: #000000;

    ${props => props.theme.breakpoints.up("tablet")} {
      color: ${props => (props.$hasBackgroundImage ? "#fff" : "#000")};
    }
  }

  ${Title} {
    color: #000000;

    ${props => props.theme.breakpoints.up("tablet")} {
      color: ${props => (props.$hasBackgroundImage ? "#fff" : "#000")};
    }
  }

  ${Description} {
    display: none;

    color: #000000;
    text-align: ${props => (props.$hasBackgroundImage ? "justify" : "left")};

    ${props => props.theme.breakpoints.up("tablet")} {
      color: ${props => (props.$hasBackgroundImage ? "#fff" : "#000")};
    }

    ${props => props.theme.breakpoints.up("desktop")} {
      display: block;
    }

    a {
      color: #ffffff;

      border-block-end: 1px solid #1b8ef8;
    }
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    > * + ${TitleContainer} {
      margin-block-start: 4px;
    }
  }
`;

export const Background = styled(BaseImage)`
  inline-size: 100%;
  block-size: auto;
  min-block-size: 100%;
  max-block-size: 520px;

  filter: brightness(60%);

  object-position: center top;
`;

// Related Links
export const StyledRelatedLinks = styled(TopicHeaderRelatedLinks)``;
export const RelatedLinksDivider = styled.div`
  block-size: 1px;

  background: #e5e5e5;
`;
export const RelatedLinksContainer = styled.div`
  position: relative;

  display: flex;
  flex-flow: row;
  justify-content: stretch;
  align-items: stretch;

  background: #ffffff;

  overflow: hidden;

  /* This creates the overflow item blur overlay indicator at the edges to indicate there is overflowed content */
  &:after,
  &:before {
    position: absolute;
    content: "";
    z-index: 1;

    inline-size: 20px;
    block-size: 100%;
  }
  &:after {
    inset-inline-end: 0;

    background: linear-gradient(270deg, #ffffff 50%, rgba(255, 255, 255, 0) 100%);
  }
  &:before {
    inset-inline-start: 0;

    background: linear-gradient(90deg, #ffffff 50%, rgba(255, 255, 255, 0) 100%);
  }

  &:empty {
    display: none;
  }
`;

// Layout
type StyledProps = {
  $hasBackgroundImage?: boolean;
};
export const Container = styled.section<StyledProps>`
  display: grid;
  grid:
    "content" min-content
    "related-links" min-content
    / 100%;

  ${Background} {
    grid-area: content;

    z-index: -1;

    display: none;
    ${props => props.theme.breakpoints.up("tablet")} {
      display: block;
    }
  }

  ${Content} {
    grid-area: content;

    inline-size: 100%;
    block-size: 100%;

    ${props => props.theme.breakpoints.up("tablet")} {
      inline-size: 50%;
      padding-block: 22px 62px;
      padding-inline-start: 40px;
    }

    ${props => props.theme.breakpoints.up("mediumDesktop")} {
      padding-inline-start: 80px;
    }
  }

  ${RelatedLinksContainer} {
    grid-area: related-links;

    ${props => props.theme.breakpoints.only("mobile")} {
      ${setFullViewportWidthWithMaxPageWidthStyles()}

      margin-block-start: 32px;
      border-block: 1px solid #d7d7d7;

      ${StyledRelatedLinks} {
        padding-inline: ${alignmentCssVariableValues.fullViewportWidthWithMaxPageWidthPaddingInline};
      }
    }

    ${props => props.theme.breakpoints.up("tablet")} {
      z-index: 2;

      justify-self: center;

      max-inline-size: calc(100% - 28px * 2);
      margin-block-start: -30px;
      margin-inline: 28px;

      border-radius: 6px;

      ${StyledRelatedLinks} {
        padding-block: 20px;
        padding-inline: 28px;
      }
    }
  }

  ${RelatedLinksDivider} {
    grid-area: related-links;

    display: none;

    ${props => props.theme.breakpoints.up("tablet")} {
      z-index: 1;

      display: ${props => (props.$hasBackgroundImage ? "none" : "block")};

      margin-inline: ${fullViewportWidthWithMaxPageWidthPaddingInline};
    }
  }

  ${RelatedLinksContainer}:empty + ${RelatedLinksDivider} {
    display: none;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    ${setFullViewportWidthWithMaxPageWidthStyles()}
    align-self: center;
  }
`;

// Drawer
export const StyledDrawer = styled(Drawer)`
  ${DrawerCard} {
    position: relative;

    inline-size: 100%;
    block-size: 241px;
    padding-block: 14px;
    padding-inline: 20px;
  }
`;

export const DrawerCardContent = styled.div`
  font-size: 14px;
  font-family: ${fontRoboto};
  line-height: 18px;

  overflow-y: auto;
`;
